import React, { useEffect, useState } from 'react';
import './addtask.css';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from "moment";
import axiosInstance from '../../services/AxiosInstance';
import TaskForm from './TaskForm';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { hasFeatureAccess, FeatureLock } from '../../utils/featureLock';
import { getCommentTaskPrefill, getForum, enrichForumTaskPrefill } from '../../services/ForumService';

let userlist = [];

const AddTask = () => {
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const [Tags, setTags] = useState([]);
  const [observers, setObservers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userdata, setuserdata] = useState();
  const [Tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [hasTaskAccess, setHasTaskAccess] = useState(false);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [prefillDocumentData, setPrefillDocumentData] = useState(null);
  const [prefillForumData, setPrefillForumData] = useState(null);
  const [forumPrefillLoading, setForumPrefillLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const prefillProjectId = searchParams.get('projectId');
  const prefillProjectData = location.state?.prefillProject || null;
  const documentId = searchParams.get('document_id');
  const forumCommentId = searchParams.get('forumCommentId');
  
  // Debug: Log documentId when it changes
  useEffect(() => {
    console.log('=== DOCUMENT PREFILL DEBUG ===');
    console.log('documentId from URL:', documentId);
    console.log('documentId type:', typeof documentId);
    console.log('Full URL search params:', Object.fromEntries(searchParams.entries()));
    console.log('Current URL:', window.location.href);
    console.log('prefillDocumentData state:', prefillDocumentData);
    console.log('================================');
    
    // Also log to make it very visible
    if (documentId) {
      console.warn('🔄 DOCUMENT ID DETECTED:', documentId);
    } else {
      console.warn('⚠️ NO DOCUMENT ID FOUND IN URL');
    }
  }, [documentId, searchParams, prefillDocumentData]);

  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    // If we came from project details (add task from project), use browser back to avoid history loop
    // Using navigate() would push a duplicate entry and cause back button to bounce between add task and project details
    if (prefillProjectId && prefillProjectData && window.history.length > 2) {
      navigate(-1);
      return;
    }
    // Default: go to task list
    navigate('/task/tasklist');
    // Set a timeout to check if we ended up on login page
    setTimeout(() => {
      if (window.location.pathname.includes('/login')) {
        const search = searchParams.get('search') || '';
        navigate(`/task/tasklist${search ? `?search=${encodeURIComponent(search)}` : ''}`);
      }
    }, 100);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setFeatureLoading(true);
        
        // Check feature access first
        const taskAccess = await hasFeatureAccess('adding_tasks');
        setHasTaskAccess(taskAccess);
        
        if (!taskAccess) {
          setFeatureLoading(false);
          setLoading(false);
          return;
        }
        
        const userData = JSON.parse(localStorage.getItem("user"));
        setuserdata(userData);

        // Fetch users for assignees, observers, and mentions
        if (userData?.corporateId) {
          const params = new URLSearchParams();
          params.append('corporateId', parseInt(userData.corporateId));
          
          // Add branch and department parameters if available
          if (userData.branchId) {
            params.append('branch', userData.branchId);
          }

          const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
          const users = response.data.users || [];

          // Filter out the currently logged-in user
          const filteredUsers = users.filter(user => user.id !== userData.id);

          setAvailableAssignees(filteredUsers);
          setTags(filteredUsers);
          setParticipants(filteredUsers);
          setObservers(filteredUsers);
          userlist = filteredUsers;
        }

        // Fetch projects
        await fetchProjects();

        // Fetch tasks
        await fetchTasks();

        // Fetch branches, departments, and sub-departments
        await fetchBranches();
        await fetchDepartments();
        await fetchSubDepartments();

        if (forumCommentId) {
          setForumPrefillLoading(true);
          try {
            const prefillRes = await getCommentTaskPrefill(forumCommentId);
            if (prefillRes.data?.success === false) {
              toast.error(prefillRes.data.message || 'Could not load discussion comment');
            } else if (prefillRes.data?.prefill) {
              let prefill = prefillRes.data.prefill;
              if (prefill.forum_id) {
                try {
                  const forumRes = await getForum(prefill.forum_id);
                  if (forumRes.data?.forum) {
                    prefill = enrichForumTaskPrefill(prefill, forumRes.data.forum);
                  }
                } catch (forumErr) {
                  console.error('Could not load discussion for task prefill:', forumErr);
                }
              }
              await hydrateForumPrefillUsers(prefill);
              setPrefillForumData(prefill);
            }
          } catch (e) {
            toast.error(e.response?.data?.message || 'Could not load discussion comment for task');
          } finally {
            setForumPrefillLoading(false);
          }
        }

        // Fetch document details if document_id is provided
        if (documentId) {
          console.log('Document ID found in URL:', documentId);
          await fetchDocumentDetails(documentId);
        } else {
          console.log('No document_id found in URL');
        }

      } catch (error) {
        console.error('Error fetching initial data:', error);
        
        // Handle 403 Forbidden error
        if (error.response && error.response.status === 409) {
          toast.error("Access denied. You don't have permission to access this data.");
          // Navigate back to task list after a short delay
          setTimeout(() => {
            navigate('/task/tasklist');
          }, 2000);
          return;
        }
        
        toast.error('Failed to load initial data');
      } finally {
        setLoading(false);
        setFeatureLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch users when document prefill data is available
  useEffect(() => {
    if (prefillDocumentData && prefillDocumentData.assigneeId) {
      const { BranchId, DepartmentId, SubDepartmentId, assigneeId } = prefillDocumentData;
      
      if (BranchId?.length > 0 || DepartmentId?.length > 0 || SubDepartmentId?.length > 0) {
        fetchUsersForPrefill(BranchId || [], DepartmentId || [], SubDepartmentId || [], assigneeId);
      } else {
        // If we have an assignee ID but no branches/departments, still try to find the user
        findUserById(assigneeId);
      }
    } else if (prefillDocumentData && !prefillDocumentData.assignee) {
      // If we have document data but no assignee yet, try to find it
      const assigneeId = prefillDocumentData.assigneeId;
      if (assigneeId) {
        findUserById(assigneeId);
      }
    }
  }, [prefillDocumentData]);

  const fetchProjects = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {
        const corporateId = userData.corporateId;

        const queryParams = new URLSearchParams();
        
        queryParams.append('corporateId', corporateId);
        
        const response = await axiosInstance.get(
          `/project/list?${queryParams.toString()}`
        );
        const data = response.data;
        setProjects(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      
      // Handle 403 Forbidden error
      if (error.response && error.response.status === 409) {
        toast.error("Access denied. You don't have permission to access project data.");
        // Navigate back to task list after a short delay
        setTimeout(() => {
          navigate('/task/tasklist');
        }, 3000);
        return;
      }
      
      toast.error('Failed to load projects');
    }
  };

  const fetchTasks = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const corporateId = userData.corporateId;

      const queryParams = new URLSearchParams();
        
      queryParams.append('corporateId', corporateId);
      const response = await axiosInstance.get(`/task/list?${queryParams.toString()}`);
      const data = response.data;
      setTasks(data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      
      // Handle 403 Forbidden error
      if (error.response && error.response.status === 409) {
        toast.error("Access denied. You don't have permission to access task data.");
        // Navigate back to task list after a short delay
        setTimeout(() => {
          navigate('/task/tasklist');
        }, 2000);
        return;
      }
      
      toast.error('Failed to load tasks');
    }
  };

  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
      const data = res.data;
      
      if (res) {
        setBranches(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load branches');
    }
  };

  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
      const data = res.data;
      
      if (res) {
        setDepartments(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
    }
  };

  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      // Try both cases for API compatibility
      let res;
      try {
        res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
      } catch (e1) {
        res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
      }
      const data = res.data;
      if (res) {
        setSubDepartments(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
      toast.error('Failed to load sub-departments');
    }
  };

  const fetchDocumentDetails = async (docId) => {
    console.log('fetchDocumentDetails called with docId:', docId);
    try {
      setDocumentLoading(true);
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      console.log('Fetching document from API:', `/documents/${docId}`);
      // Try to fetch document details
      let response;
      try {
        response = await axiosInstance.get(`/documents/${docId}`);
        console.log('Document API response:', response);
      } catch (error) {
        console.log('Primary API call failed, trying fallback:', error);
        // Fallback: fetch from list and filter by ID
        const params = new URLSearchParams();
        params.append('corporate_id', corporateId);
        const listResponse = await axiosInstance.get(`/documents/list?${params.toString()}`);
        
        if (listResponse.status === 200 && listResponse.data.success) {
          const documents = listResponse.data.data.documents || [];
          const document = documents.find(doc => doc.id === parseInt(docId));
          
          if (document) {
            preparePrefillData(document);
            return;
          }
        }
        throw error;
      }

      if (response && response.status === 200) {
        const data = response.data;
        console.log('Document response data:', data);
        // Handle nested structure: response.data.data or response.data
        const document = data.data || data;
        console.log('Extracted document object:', document);
        
        // Check if it's the wrapped structure with success field
        if (data.success && data.data) {
          console.log('Using wrapped structure (data.success && data.data)');
          preparePrefillData(data.data);
        } else {
          console.log('Using direct document structure');
          preparePrefillData(document);
        }
      } else {
        console.log('Unexpected response status:', response?.status);
      }
    } catch (error) {
      console.error('Error fetching document details:', error);
      toast.error('Failed to load document details. Task form will open without prefilled data.');
    } finally {
      setDocumentLoading(false);
    }
  };

  const preparePrefillData = (document) => {
    console.log('preparePrefillData called with document:', document);
    try {
      // Extract branches, departments, sub-departments IDs
      // Handle different possible structures from API
      const branchIds = (document.branches || []).map(branch => {
        if (typeof branch === 'object') {
          return branch.id || branch.branch_id || branch.Branch?.id;
        }
        return branch; // If it's just an ID number
      }).filter(Boolean);
      
      const departmentIds = (document.departments || []).map(dept => {
        if (typeof dept === 'object') {
          return dept.id || dept.department_id || dept.Department?.id;
        }
        return dept; // If it's just an ID number
      }).filter(Boolean);
      
      // Handle both subdepartments and sub_departments field names
      const subDepartmentIds = (document.subdepartments || document.sub_departments || []).map(subDept => {
        if (typeof subDept === 'object') {
          return subDept.id || subDept.sub_department_id || subDept.SubDepartment?.id;
        }
        return subDept; // If it's just an ID number
      }).filter(Boolean);

      // Find assignee from uploaded_by or assigned_to
      // uploaded_by can be a number or an object with id property
      let assigneeId = null;
      if (typeof document.uploaded_by === 'number') {
        assigneeId = document.uploaded_by;
      } else if (document.uploaded_by_id) {
        assigneeId = document.uploaded_by_id;
      } else if (document.uploaded_by?.id) {
        assigneeId = document.uploaded_by.id;
      } else if (document.uploaded_by_user?.id) {
        assigneeId = document.uploaded_by_user.id;
      } else if (document.assigned_to_id) {
        assigneeId = document.assigned_to_id;
      } else if (typeof document.assigned_to === 'number') {
        assigneeId = document.assigned_to;
      } else if (document.assigned_to?.id) {
        assigneeId = document.assigned_to.id;
      }

      // Prepare prefilled data
      const prefillData = {
        TaskName: document.document_title || document.name || document.file_name || '',
        BranchId: branchIds,
        DepartmentId: departmentIds,
        SubDepartmentId: subDepartmentIds,
        assigneeId: assigneeId,
        expiryDate: document.expiry_date || null, // Document expiry date for task deadline
        document: document // Store full document for reference
      };

      // If uploaded_by_user is available in document, use it directly for assignee
      let assignee = null;
      if (document.uploaded_by_user && assigneeId && 
          (document.uploaded_by_user.id === assigneeId || parseInt(document.uploaded_by_user.id) === parseInt(assigneeId))) {
        assignee = {
          id: document.uploaded_by_user.id,
          CorporateUserId: document.uploaded_by_user.id,
          FullName: document.uploaded_by_user.full_name || document.uploaded_by_user.fullName,
          Email: document.uploaded_by_user.email,
          full_name: document.uploaded_by_user.full_name || document.uploaded_by_user.fullName,
          email: document.uploaded_by_user.email,
          name: document.uploaded_by_user.full_name || document.uploaded_by_user.fullName
        };
      }
      
      // Add assignee to prefill data if available
      if (assignee) {
        prefillData.assignee = assignee;
      }
      
      console.log('Prefill data prepared:', prefillData);
      setPrefillDocumentData(prefillData);
      console.log('prefillDocumentData state set:', prefillData);
      
      // Fetch users based on selected branches/departments for filtering
      // This will be handled in a useEffect after prefillDocumentData is set
    } catch (error) {
      console.error('Error preparing prefill data:', error);
    }
  };

  const hydrateForumPrefillUsers = async (prefill) => {
    if (!prefill) return;
    const { BranchId, DepartmentId, SubDepartmentId, assignees } = prefill;
    if (BranchId?.length > 0 || DepartmentId?.length > 0 || SubDepartmentId?.length > 0) {
      await fetchUsersForForumPrefill(
        BranchId || [],
        DepartmentId || [],
        SubDepartmentId || [],
        assignees || []
      );
    }
  };

  const fetchUsersForForumPrefill = async (branchIds, departmentIds, subDepartmentIds, knownAssignees = []) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData?.corporateId) return;

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(userData.corporateId, 10));
      if (branchIds.length > 0) params.append('branch', branchIds.join(','));
      if (departmentIds.length > 0) params.append('department', departmentIds.join(','));
      if (subDepartmentIds.length > 0) params.append('sub_department', subDepartmentIds.join(','));

      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      const filteredUsers = users.filter((user) => user.id !== userData.id);

      const mergeUserLists = (base, extras) => {
        const merged = [...base];
        extras.forEach((extra) => {
          if (!extra?.id) return;
          if (!merged.some((u) => String(u.id) === String(extra.id))) {
            merged.push(extra);
          }
        });
        return merged;
      };

      const dropdownUsers = mergeUserLists(filteredUsers, knownAssignees);
      setAvailableAssignees(dropdownUsers);
      setTags(dropdownUsers);
      setParticipants(dropdownUsers);
      setObservers(dropdownUsers);
      userlist = dropdownUsers;

    } catch (error) {
      console.error('Error fetching users for forum prefill:', error);
    }
  };

  const fetchUsersForPrefill = async (branchIds, departmentIds, subDepartmentIds, assigneeId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData?.corporateId) return;

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(userData.corporateId));
      
      // Add branch and department filters
      if (branchIds.length > 0) {
        branchIds.forEach(branchId => {
          params.append('branch', branchId);
        });
      }
      if (departmentIds.length > 0) {
        departmentIds.forEach(deptId => {
          params.append('department', deptId);
        });
      }

      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      const filteredUsers = users.filter(user => user.id !== userData.id);

      // Update available assignees to include filtered users
      setAvailableAssignees(filteredUsers);
      setTags(filteredUsers);
      setParticipants(filteredUsers);
      setObservers(filteredUsers);
      userlist = filteredUsers;

      // If assigneeId is provided, find and add to prefill data
      if (assigneeId) {
        const assignee = filteredUsers.find(u => 
          u.id === assigneeId || 
          u.CorporateUserId === assigneeId ||
          parseInt(u.id) === parseInt(assigneeId) ||
          parseInt(u.CorporateUserId) === parseInt(assigneeId)
        );
        if (assignee) {
          setPrefillDocumentData(prev => ({
            ...prev,
            assignee: assignee
          }));
        } else {
          // If assignee not found in filtered users, try to find in all available users
          // This handles cases where user might not match branch/department filters
          console.log('Assignee not found in filtered users, trying to find in all users');
        }
      }
    } catch (error) {
      console.error('Error fetching users for prefill:', error);
    }
  };

  const findUserById = async (userId, prefillType = 'document') => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData?.corporateId) return;

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(userData.corporateId));
      
      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      const assignee = users.find(u => 
        u.id === userId || 
        u.CorporateUserId === userId ||
        parseInt(u.id) === parseInt(userId) ||
        parseInt(u.CorporateUserId) === parseInt(userId)
      );
      
      if (assignee) {
        // Also update available assignees if not already included
        setAvailableAssignees(prev => {
          const exists = prev.find(u => u.id === assignee.id || u.CorporateUserId === assignee.id);
          if (!exists) {
            return [...prev, assignee];
          }
          return prev;
        });

        if (prefillType !== 'forum') {
          setPrefillDocumentData(prev => ({
            ...prev,
            assignee: assignee
          }));
        }
      } else {
        // If user not found in corporate users, try to create user object from document data
        if (prefillDocumentData?.document?.uploaded_by_user) {
          const uploadedByUser = prefillDocumentData.document.uploaded_by_user;
          if (uploadedByUser.id === userId || parseInt(uploadedByUser.id) === parseInt(userId)) {
            // Create a user object compatible with the assignee format
            const userObj = {
              id: uploadedByUser.id,
              CorporateUserId: uploadedByUser.id,
              FullName: uploadedByUser.full_name,
              Email: uploadedByUser.email,
              full_name: uploadedByUser.full_name,
              email: uploadedByUser.email
            };
            
            setAvailableAssignees(prev => {
              const exists = prev.find(u => u.id === userObj.id);
              if (!exists) {
                return [...prev, userObj];
              }
              return prev;
            });
            
            setPrefillDocumentData(prev => ({
              ...prev,
              assignee: userObj
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error finding user by ID:', error);
    }
  };

  const handleSubmit = async (values, setSubmitting) => {
    const formData = new FormData();
    const statusMap = {
      'Pending': 0,
      'In Progress': 1,
      'Postponed': 2,
      'Done': 3,
    };
    const numericStatus = 0;

    if (userdata) {
      // Helper function to convert 12-hour to 24-hour format
      const convert12To24Hour = (time12) => {
        if (!time12) return '00:00';
        const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return '00:00';
        
        let hour = parseInt(match[1]);
        const minute = parseInt(match[2]);
        const ampm = match[3].toUpperCase();
        
        if (ampm === 'PM' && hour !== 12) {
          hour += 12;
        } else if (ampm === 'AM' && hour === 12) {
          hour = 0;
        }
        
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      };

      // Helper function to format date with time if dates are the same
      const formatDateWithTime = (dateValue, timeValue, isEndDate = false) => {
        if (!dateValue) return '';
        const dateStr = moment(dateValue).format('YYYY-MM-DD');
        // If StartDate === Deadline and time is provided, combine date and time
        if (values.StartDate && values.Deadline && values.StartDate === values.Deadline && timeValue) {
          const time24 = convert12To24Hour(timeValue);
          // Ensure 12:00 PM converts to 12:00, not 00:00
          return `${dateStr} ${time24}:00`;
        }
        // Default to 00:00:00 if no time or dates are different
        return `${dateStr} 00:00:00`;
      };

      // Batch fields to append
      const fieldsToAppend = {
        TaskName: values.TaskName,
        ProjectId: values.ProjectId,
        Description: values.Description,
        Priority: values.Priority,
        Status: numericStatus,
        StartDate: formatDateWithTime(values.StartDate, values.StartTime, false),
        EndDate: formatDateWithTime(values.Deadline, values.EndTime, true),
        RepeatType: values.RepeatType,
        RepeatInterval: values.RepeatInterval,
        RepeatUntil: values.RepeatUntil ? moment(values.RepeatUntil).format('YYYY-MM-DD') : '',
        RepeatNeverEnds: values.RepeatNeverEnds || false,
        RepeatWeekDays: values.WeeklyDays ? JSON.stringify(values.WeeklyDays) : null,
        MonthlyRepeatType: values.MonthlyRepeatType || null,
        AllowAssigneeUpdate: values.is_assignee_update_allowed,
        SendWarning: values.email_reminder,
        CloseChecklist: values.CloseChecklist,
        CreatedBy: userdata?.id,
        CreatedName: userdata?.name,
        IsRepeatable: values?.IsRepeatable ? 1 : 0,
        CorporateId: parseInt(userdata.corporateId),
        BranchId: values.BranchId || null,
        DepartmentId: values.DepartmentId || null,
        CreateAsIndividual: values.CreateAsIndividual || false,
      };

      // Only append Delta if it exists and is not null
      if (values.Delta) {
        fieldsToAppend.Delta = values.Delta;
      }

      Object.entries(fieldsToAppend).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          formData.append(key, value);
        }
      });

      // Special cases
      if (values.Mentions && values.Mentions.length) {
        const result = values.Mentions.map(item1 => {
          const match = availableAssignees.find(item2 => item2.CorporateUserId === parseInt(item1.id));
          if (match) {
            return {
              ...match,
              start: item1.start,
              end: item1.end
            };
          }
          return null;
        }).filter(Boolean);
        formData.append('Mentions', JSON.stringify(result));
      }

      if (values.Tags && values.Tags.length) {
        formData.append('Tags', JSON.stringify(values.Tags));
      }

      if (values.assigneeList && values.assigneeList.length) {
        formData.append('Assignees', JSON.stringify(values.assigneeList));
      }

      if (values.Participants && values.Participants.length) {
        formData.append('Participants', JSON.stringify(values.Participants));
      }

      if (values.Observers && values.Observers.length) {
        formData.append('Observers', JSON.stringify(values.Observers));
      }

      const filteredChecklist = values.Checklist.filter(
        item => item.value && item.value.trim() !== ""
      ).map((item, index) => ({
        ChecklistItem: item.value,
        IsCompleted: item.checked,
        SortOrder: index + 1
      }));

      if (filteredChecklist.length) {
        formData.append('ChecklistJSON', JSON.stringify(filteredChecklist));
      }

      if (values.reminderinternalmessage) {
        formData.append('ReminderInternal', values.reminderinternalmessage);
      }

      if (values.reminderthrougthemail) {
        formData.append("ReminderEmailFlag", values.reminderthrougthemail);
      }

      if (values.MarkHighPriority) {
        formData.append("HighPriority", values.MarkHighPriority);
      }

      if (values.TaskPin) {
        formData.append("PinTask", values.TaskPin);
      }
      console.log(values.Files);
      values.Files.forEach(fileObj => {
        let fileToSend = fileObj;

        // Convert if it's not a real File
        if (!(fileObj instanceof File)) {
          const blob = new Blob([], { type: fileObj.type }); // empty if original data isn't available
          fileToSend = new File([blob], fileObj.name, {
            type: fileObj.type,
            lastModified: fileObj.lastModified
          });
        }

        formData.append('Files', fileToSend);
      });

      formData.append("ParentTaskId", values.ParentTaskId !== '' ? values.ParentTaskId : null);

      // Append Sub-Departments if selected
      if (values.SubDepartmentId && values.SubDepartmentId.length) {
        formData.append('SubDepartmentId', values.SubDepartmentId);
      }

      try {
        const response = await axiosInstance.post('/task/addtask', formData);
        const result = response.data;
        if (result && result.success === false) {
          toast.error(result.error || result.message || 'Failed to create task');
          setSubmitting(false);
        } else if (response) {
          toast.success("Task added successfully");
          setIsRedirecting(true);
          setSubmitting(false);
          // Add 2-second delay before redirecting
          setTimeout(() => {
            navigateBackSafely();
            
          }, 2000);
        } else {
          toast.error(result?.message || 'Failed to create task');
          setSubmitting(false);
        }
      } catch (error) {
        toast.error(error.response?.data?.error || error.response?.data?.message || error.message || 'Error during API call');
        setSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    navigateBackSafely();
  };

  if (loading || featureLoading || forumPrefillLoading || (documentId && documentLoading)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show feature locked message if user doesn't have access
  if (!hasTaskAccess) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <div className="feature-locked">
                    <i className="fas fa-lock text-warning" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
                    <h4 className="text-muted mb-3">Adding Tasks Not Available</h4>
                    <p className="text-muted mb-4">
                      Adding tasks is not available in your current subscription plan.
                      Upgrade your plan to access this feature.
                    </p>
                    <div className="d-flex gap-3 justify-content-center">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/task/tasklist')}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Task List
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate('/user-subscription')}
                      >
                        <i className="fas fa-crown me-2"></i>
                        Upgrade Plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          navigateBackSafely();
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <TaskForm
        mode="add"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        availableAssignees={availableAssignees}
        Tags={Tags}
        observers={observers}
        participants={participants}
        projects={projects}
        Tasks={Tasks}
        userdata={userdata}
        isRedirecting={isRedirecting}
        prefillProjectId={prefillProjectId}
        prefillProjectData={prefillProjectData}
        prefillDocumentData={prefillDocumentData}
        prefillForumData={prefillForumData}
        branches={branches}
        departments={departments}
        subDepartments={subDepartments}
      />
      <InternalFooter />
    </div>
  );
};

export default AddTask;